import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { PageAbsolutePath } from '../../../../consts/Common';
import { ButtonContinue } from '../../../common/Button/ButtonContinue';
import Table, { HeaderRow, TableRow } from '../../../common/Widgets/Table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons/faCheck';
import { faXmark } from '@fortawesome/free-solid-svg-icons/faXmark';
import List from '../../../common/Widgets/List';
import Card from '../../../common/Widgets/Card';
import { SiteContext } from '../../../../context/SiteContext';
import MobiliseSecurityDialog from '../MobiliseSecurityDialog';
import MobiliseWelcomeDialog from '../MobiliseWelcomeDialog';
import {
  InsuranceJourneyContext,
  InsuranceJourneyState
} from '../../../common/CommonTypes';
import useCarInsuranceController from '../../../../hooks/insuranceJourney/useCarInsuranceController';
import { getStartQuotePath } from '../InsuranceMethods';
import { useErrorBoundary } from 'react-error-boundary';

const NissanLandingPage = (): JSX.Element => {
  const [showModalDialog, setShowModalDialog] = useState(false);

  const [showWelcomeDialog, setShowWelcomeDialog] = useState(false);

  const navigate = useNavigate();
  const { showBoundary } = useErrorBoundary();

  const { site } = useContext(SiteContext);

  const { isInsuranceMobilise, brand } = site;

  const phoneNumber = brand.brandPhoneNumbers.quotesAndRenewalsNumber;

  const { pageEvents } = useOutletContext<InsuranceJourneyContext>();

  const { commonEvents } = pageEvents;

  const { setQuote } = commonEvents;

  const { getNewQuote } = useCarInsuranceController();

  const getNewQuoteSuccess = (quote: InsuranceJourneyState) => {
    setQuote({
      ...quote,
      tiaProductCode: site.brand.tiaProductCode
    });

    navigate(
      getStartQuotePath(
        site.user.isDealer ?? false,
        site.brand.useAlternateEligibility
      )
    );
  };

  const onBackendFailure = (err: string) => {
    showBoundary(new Error(err));
  };

  const getStarted = () => {
    getNewQuote(
      site.brand.brandName,
      site.user?.insuranceDealerCode ?? '',
      site.user.token,
      getNewQuoteSuccess,
      onBackendFailure
    );
  };

  const getStartedRetrieveQuote = () => {
    navigate(PageAbsolutePath.Insurance.RetrieveQuote);
  };

  const nissanTableHeader: HeaderRow = {
    columns: ['Nissan Guaranteed', 'Nissan Insurance', 'Top UK Insurers']
  };

  const tickComponent = <FontAwesomeIcon icon={faCheck} />;
  const crossComponent = <FontAwesomeIcon icon={faXmark} />;
  const listIcon = <FontAwesomeIcon icon={faCheck} listItem />;

  const nissanTableRows: TableRow[] = [
    { columns: ['Nissan genuine parts', tickComponent, crossComponent] },
    { columns: ['Nissan approved repairers', tickComponent, crossComponent] },
    { columns: ['Nissan trained technicians', tickComponent, crossComponent] },
    {
      columns: [
        'Nissan manufacturer warranty and safety rating preserved by only fitting Nissan genuine parts',
        tickComponent,
        crossComponent
      ]
    }
  ];

  const standardCoverItems: string[] = [
    'Comprehensive cover',
    'Courtesy car (subject to availability)',
    '24-hour accident recovery',
    'Comprehensive EU cover',
    'Personal accident cover',
    'Replacement lock cover',
    '5 year repair guarantee',
    'Medical cover',
    'Personal possessions cover',
    'Windscreen damage cover',
    'A brand new Nissan if yours is written off or stolen within 12 months of its new registration date'
  ];

  const extraCoverItems: string[] = [
    '£100,000 legal expenses',
    'No-claim discount protection',
    'Upgrade your courtesy car and stay mobile if your car is written-off or stolen',
    'Breakdown cover (provided by Allianz Global Assistance)'
  ];

  const onCloseModal = () => {
    setShowModalDialog(false);
  };

  const onContinueModal = () => {
    onCloseModal();
  };

  const onShowModal = () => {
    setShowModalDialog(true);
  };

  const renderLoginButtons = (
    additionalClass: string,
    isHeroLogin: boolean
  ) => {
    const className = 'flex-row ' + additionalClass;
    if (isInsuranceMobilise) {
      return (
        <div className={className}>
          <ButtonContinue
            name={isHeroLogin ? 'btnCompleteQuote' : 'btnCompleteQuote2'}
            text='Complete Quote'
            onClick={onShowModal}
            additionalClassNames={isHeroLogin ? 'btn-hero-start' : 'btn-start'}
          />
        </div>
      );
    } else {
      return (
        <div className={className}>
          <ButtonContinue
            name={isHeroLogin ? 'btnGetStarted' : 'btnGetStarted2'}
            text='Start Quote'
            onClick={getStarted}
            additionalClassNames={isHeroLogin ? 'btn-hero-start' : 'btn-start'}
          />
          <ButtonContinue
            name={isHeroLogin ? 'btnRetrieveQuote' : 'btnRetrieveQuote2'}
            text='Retrieve Quote'
            onClick={getStartedRetrieveQuote}
            additionalClassNames={
              isHeroLogin
                ? 'btn-hero-retrieve m05-left'
                : 'btn-retrieve m05-left'
            }
          />
        </div>
      );
    }
  };

  const onCloseWelcomeDialog = () => {
    setShowWelcomeDialog(false);
  };

  useEffect(() => {
    setShowWelcomeDialog(isInsuranceMobilise);
  }, []);

  return (
    <div className='insurance-landing-page primary-section'>
      <div className='hero-image'>
        <h1 className='image-text main-header'>Welcome to Nissan Insurance</h1>
        {renderLoginButtons('mobile-hide', true)}
        <h2 className='mobile-hide image-text'>
          Or call{' '}
          <a className='calltext' href={`tel:${phoneNumber}`}>
            {phoneNumber}
          </a>
        </h2>
      </div>
      <div className='flex-row justify-center'>
        <h3>Why choose us?</h3>
        <div className='flex-row cards'>
          <div className='whycard'>
            <Card
              id='why1'
              title='Genuine parts'
              description='Guaranteed Nissan Genuine parts to keep your Nissan 100% original'
            />
          </div>
          <div className='whycard'>
            <Card
              id='why2'
              title='Approved repairs'
              description='Only carried out at Nissan Approved Bodyshops and guaranteed to meet Nissan standards'
            />
          </div>
          <div className='whycard'>
            <Card
              id='why3'
              title='Preserved warranty'
              description='Your car stays protected and its safety rating is maintained'
            />
          </div>
        </div>
      </div>
      <div className='flex-row justify-center secondary-section'>
        <div className='margin-panel'>
          <h4>Think all insurers are the same? Think again.</h4>
          <p>
            Our exclusive repair benefits guarantee what many insurers don't.
          </p>
          <Table
            id='landing-page-table'
            header={nissanTableHeader}
            rows={nissanTableRows}
            addendum='Source: Research of UK motor insurer policy wordings, August 2024.'
          />
          <div className='flex-row'>
            <div className='two-columns'>
              <List
                id='standard-cover'
                title='Standard Cover'
                listItems={standardCoverItems}
                icon={listIcon}
              />
            </div>
            <div className='two-columns'>
              <List
                id='extra-cover'
                title='Optional Extra Cover'
                listItems={extraCoverItems}
                icon={listIcon}
              />
            </div>
          </div>
        </div>
      </div>
      <div className='stickyButtons'>
        {renderLoginButtons('justify-center', false)}
        <div className='flex-row justify-center contact'>
          <p>
            Or call{' '}
            <a className='calltext' href={`tel:${phoneNumber}`}>
              {phoneNumber}
            </a>
          </p>
        </div>
      </div>
      <MobiliseSecurityDialog
        showDialog={showModalDialog}
        onCloseModal={onCloseModal}
        onContinueModal={onContinueModal}
        cultureCode={site.cultureCode}
        brand={brand.brandName}
        phoneNumber={phoneNumber}
      />
      <MobiliseWelcomeDialog
        showDialog={showWelcomeDialog}
        onCloseModal={onCloseWelcomeDialog}
        brand={brand.brandName}
      />
    </div>
  );
};

export default NissanLandingPage;
